'use client';

import { FilterAnd, FilterEqual, SearchResultsWidgetQuery } from '@sitecore-search/react';

// Global
import {
  WidgetDataType,
  useSearchResults,
  widget,
  SearchResultsInitialState,
} from '@sitecore-search/react';
import { tv } from 'tailwind-variants';

// Local
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { PageSpecific } from '.generated/Feature.HztlFoundation.model';
import ArticleCardWrapper, {
  ArticleModel,
} from 'helpers/GenericWrappers/ArticleCardWrapper/ArticleCardWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import { useEffect, useRef, useState, useTransition } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { SearchLoading } from 'helpers/SearchLoading/SearchLoading';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { PageTypes } from '.generated/Project.HztlFoundation.model';
import { getTestProps } from 'lib/testing/utils';

type InitialState = SearchResultsInitialState<'itemsPerPage' | 'page'>;

export type ArticleListingProps = PageSpecific.ArticleListing_Component & {
  staticProps: {
    currentTemplate: string;
    currentCategory: string;
  };
  searchSources?: string[];
};

export const ArticleSearchResultsComponent = (props: ArticleListingProps) => {
  // Defensive: in App Router with Content SDK 2.0 the SDK does not auto-inject
  // `getComponentServerProps` results; the page-specific wrapper now reads them
  // from ComponentPropsContext and forwards them here. Guard so a missing
  // `staticProps` does not throw and freeze the listing in its skeleton state.
  const isCategoryPage = props?.staticProps?.currentTemplate === 'Article Category Page';
  const currentCategory = props?.staticProps?.currentCategory ?? '';
  const {
    widgetRef,
    actions: { onPageNumberChange, onItemClick },
    query,
    state: { page },
    queryResult: {
      isFetching,
      isLoading,
      isSuccess,
      data: { content: articles, total_item: totalItems = 0 } = {},
    },
  } = useSearchResults<ArticleModel, InitialState>({
    state: {
      page: 1,
      itemsPerPage: 12,
    },
    query: (query: SearchResultsWidgetQuery) => {
      const filters = [new FilterEqual('type', 'articles')];
      if (isCategoryPage && currentCategory) {
        filters.push(new FilterEqual('article_category', currentCategory));
      }
      const finalFilter = filters.length > 1 ? new FilterAnd(filters) : filters[0];
      query.getRequest().setSearchFilter(finalFilter);
      // Use provided sources if available
      if (props?.searchSources && props.searchSources?.length > 0) {
        query.getRequest().setSources(props.searchSources);
      }
      return query;
    },
  });

  const [articlesList, setArticlesList] = useState<ArticleModel[]>([]);
  // Wrap result commits in a transition so a parent <Suspense> boundary
  // doesn't fall back to its skeleton while React commits the new list.
  const [, startResultTransition] = useTransition();
  const { getDictionaryValue } = useDictionary();
  const pathname = usePathname() ?? '';
  const searchParams = useSearchParams();
  const routeLocationKey = `${pathname}?${searchParams?.toString() ?? ''}`;

  // ── STABLE REFS: `query` and `onPageNumberChange` from useSearchResults
  // change identity on every render. Putting them in a useEffect dep array
  // (as the original code did) caused the reset effect to fire on every
  // render, repeatedly calling setArticlesList([]) + onPageNumberChange({
  // page: 1 }), which in turn re-triggered the SDK fetch and kept
  // `isFetching` stuck at true forever — leaving the skeleton on screen
  // and never committing the loaded articles to the DOM.
  const queryRef = useRef(query);
  queryRef.current = query;
  const onPageNumberChangeRef = useRef(onPageNumberChange);
  onPageNumberChangeRef.current = onPageNumberChange;

  // Reset list + reissue search ONLY when the route/category actually
  // changes — not on every render-induced identity churn of the SDK
  // hook's return values.
  const lastResetKeyRef = useRef<string>('');
  useEffect(() => {
    const resetKey = `${routeLocationKey}|${currentCategory}|${isCategoryPage ? '1' : '0'}`;
    if (lastResetKeyRef.current === resetKey) {
      return;
    }
    lastResetKeyRef.current = resetKey;

    const filters = [new FilterEqual('type', 'articles')];
    if (currentCategory && isCategoryPage) {
      filters.push(new FilterEqual('article_category', currentCategory));
    }
    queryRef.current.getRequest().setSearchFilter(new FilterAnd(filters));
    setArticlesList([]);
    if (onPageNumberChangeRef.current) {
      onPageNumberChangeRef.current({ page: 1 });
    }
  }, [routeLocationKey, currentCategory, isCategoryPage]);

  // Commit incoming page of articles in a transition so the parent Suspense
  // boundary doesn't fall back to its loading state while the list grows.
  useEffect(() => {
    if (isSuccess && articles && articles.length > 0) {
      startResultTransition(() => {
        setArticlesList((prev) => prev.concat(articles));
      });
    }
  }, [articles, isSuccess]);

  const {
    base,
    mainTitleStyle,
    articleCardWrapper,
    viewMoreButtonWrapper,
    viewMoreButton,
    noArticleStyle,
    loadMoreTextStyle,
  } = TAILWIND_VARIANTS();

  const { sitecoreContext } = useSitecoreContext();
  const currentPage = sitecoreContext?.route as PageTypes.ArticleLandingPage;
  const { articleListTitle } = currentPage?.fields ?? {};

  return (
    <div ref={widgetRef} className={base()}>
      <PlainTextWrapper
        className={mainTitleStyle()}
        field={articleListTitle}
        tag="h2"
        {...getTestProps(`headline`)}
      />

      {!isLoading && articlesList?.length > 0 ? (
        <div className={articleCardWrapper()}>
          {articlesList.map((article, index) => {
            return (
              <ArticleCardWrapper
                key={article.id}
                article={article}
                isHorizontalCardLayout={false}
                onItemClick={() => {
                  onItemClick?.({
                    id: article.id,
                    index,
                    // entity type registered on the widget HOC
                    entity: 'content',
                  });
                }}
              />
            );
          })}
        </div>
      ) : (
        !isLoading && (
          <PlainTextWrapper
            className={noArticleStyle()}
            field={{ value: getDictionaryValue('NoArticles') || 'No articles' }}
            tag="p"
            {...getTestProps(`no-articles-text`)}
          />
        )
      )}
      <SearchLoading isLoading={isFetching} />
      {articlesList.length !== totalItems && !isFetching && (
        <div className={viewMoreButtonWrapper()}>
          <button
            className={viewMoreButton()}
            onClick={() => {
              onPageNumberChange({ page: page + 1 });
            }}
            aria-label={`Load more articles, ${totalItems - articlesList.length} remaining`}
            {...getTestProps(`view-more-btn`)}
          >
            <SvgIcon icon="accordion-plus" size="s" />
            <PlainTextWrapper
              className={loadMoreTextStyle()}
              field={{ value: getDictionaryValue('LoadMore') || 'Load More' }}
              tag="p"
              {...getTestProps(`view-more-text`)}
            />
          </button>
        </div>
      )}
    </div>
  );
};

const ArticleSearchWidget = widget(
  ArticleSearchResultsComponent,
  WidgetDataType.SEARCH_RESULTS,
  'content'
);
export default ArticleSearchWidget;

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['flex', 'flex-col', 'gap-6'],
    mainTitleStyle: [
      'text-component-feature-title',
      'font-typography-font-family-header',
      'text-typography-font-size-medium',
      'font-bold',
      'leading-[38.4px]',
    ],
    articleCardWrapper: [
      'grid',
      'grid-cols-1',
      'sm:grid-cols-2',
      'lg:grid-cols-4',
      'gap-6',
      'm-auto',
    ],
    viewMoreButtonWrapper: ['m-auto'],
    viewMoreButton: [
      'flex',
      'items-center',
      'py-spacing-spacing-16',
      'px-spacing-spacing-24',
      'gap-spacing-spacing-8',
      'border-general-border-width-button',
      'border-component-button-primary-outline-border',
      'rounded-general-border-radius-button',
      'bg-component-button-primary-outline-bg',
      'hover:bg-component-button-primary-outline-bg-hover',
      'hover:border-general-border-width-button',
      'hover:component-button-primary-outline-border-hover',
    ],
    noArticleStyle: ['text-center', 'text-lg', 'font-semibold', 'text-color-text-text'],
    loadMoreTextStyle: [
      'text-component-button-primary-outline-text',
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'font-semibold',
      'leading-6',
    ],
  },
});
